import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { createSelector } from "reselect"
import type { StateObject } from "../../redux/types"
import type { CsvStatusType } from "./reducer"
import type {
    CategoriesListItem,
    CategoriesListResponse,
    CategoryModifierGroupResponse,
    CategoryModifierResponse,
    CategoryOptionGroupResponse,
} from "../../../graphql/types"
import { isItemsAndCategoriesAgendaOpened, isSchematizedAgendaOpened } from "../../data/selectors"
import type { UnmatchedOrderItemNamesResponse } from "./itemsCategories/detail/request"
import { getSelectedLocationOverrideId } from "../../common/locationsPicker/selectors"
import { areLocationsAuthDataLoaded, getSelectedAccountId } from "../../auth/selectors"

export const getItemsCategoriesState = (state: StateObject) => state.itemsCategories

export const getItemsCategoriesSearchValue = (state: StateObject): Maybe<string> =>
    getItemsCategoriesState(state).searchValue

export const getNewlyCreatedOptionGroupId = (state: StateObject): Maybe<string> =>
    getItemsCategoriesState(state).newlyCreatedOptionGroupId

export const getNewlyCreatedModifierGroupId = (state: StateObject): Maybe<string> =>
    getItemsCategoriesState(state).newlyCreatedModifierGroupId

export const getSendingCsvStatus = (state: StateObject): CsvStatusType =>
    getItemsCategoriesState(state).csvImportedStatus

export const getOptionGroupsData = (state: StateObject): Maybe<CategoryOptionGroupResponse> =>
    getItemsCategoriesState(state).optionGroups

export const getModifierGroupsData = (state: StateObject): Maybe<CategoryModifierGroupResponse> =>
    getItemsCategoriesState(state).modifierGroups

export const getModifiersData = (state: StateObject): Maybe<CategoryModifierResponse> =>
    getItemsCategoriesState(state).modifiers

export const getItemsAndCategoriesListResponse = (state: StateObject): Maybe<CategoriesListResponse> =>
    getItemsCategoriesState(state).itemsAndCategoriesList

const toIdSet = (items: Maybe<Array<{ id: string }>>): Set<string> => new Set(items?.map(item => item.id))

export const getExistingModifierIds = createSelector(getModifiersData, response => toIdSet(response?.data))

export const getExistingModifierGroupIds = createSelector(getModifierGroupsData, response => toIdSet(response?.data))

export const getExistingOptionGroupIds = createSelector(getOptionGroupsData, response => toIdSet(response?.data))

export const getExistingItemAndCategoryIds = createSelector(getItemsAndCategoriesListResponse, response =>
    toIdSet(response?.data)
)

export const getItemsAndCategoriesListDataCount = (state: StateObject): number =>
    getItemsCategoriesState(state).itemsAndCategoriesList?.data.length ?? 0

export const getItemsAndCategoriesListLocationId = (state: StateObject): Maybe<string> =>
    getItemsCategoriesState(state).itemsAndCategoriesListLocationId

export const getItemsAndCategoriesList = (state: StateObject): CategoriesListItem[] => {
    const response = getItemsAndCategoriesListResponse(state)
    return response ? response.data : []
}

export const shouldDownloadItemsAndCategoriesData = (state: StateObject): boolean =>
    (isItemsAndCategoriesAgendaOpened(state) || isSchematizedAgendaOpened(state)) &&
    !!getSelectedAccountId(state) &&
    areLocationsAuthDataLoaded(state)

export const shouldDownloadLocationOverrideData = (state: StateObject): boolean => {
    const locationId = getSelectedLocationOverrideId(state)
    const overrides = getItemsAndCategoriesListResponse(state)?.overrides
    // This is just quick check to avoid redownloading overrides items and categories data.
    // Note: It has a gap, though, when there are 0 overrides for location - meaning overrides = [],
    // this function will evaluate as 'true' because no overrides are found obviously, and redownload
    // of data happens always when location is selected.
    return !overrides?.find(o => o.locationId === locationId)
}

export const getUnmatchedOrderItemNames = (state: StateObject): Maybe<UnmatchedOrderItemNamesResponse> =>
    getItemsCategoriesState(state)
        // Quickfix: for some reason one of the unmatched names is empty string "" and it causes error in the UI.
        // Proper fix would be investigating how this empty string gets there in the first place.
        .unmatchedOrderItemNames?.filter(name => !!name)
